import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const FILTERS = ["All", "Solar", "EV Charging", "Green Hydrogen", "EPC", "Healthcare"];


export default function Projects() {
  useSEO({
    title: "Projects",
    path: "/projects",
    description:
      "Solar plants, EV charging, green hydrogen, EPC and healthcare projects delivered by GENOUM GROUP across India.",
  });
  const [filter, setFilter] = useState("All");
  const [projects, setProjects] = useState(null);
  const filtered = (projects || []).filter((p) => filter === "All" || p.division === filter);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, []);

  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-navy min-h-screen text-slate-50" data-testid="projects-page">
      <Navbar />
      <main className="pt-36 pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono-accent text-xs tracking-[0.35em] uppercase text-neon mb-6"
          >
            Project Showcase
          </motion.p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95]" data-testid="projects-heading">
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              >
                Delivered. <span className="text-transparent bg-clip-text bg-gradient-to-r from-electric to-neon">Proven.</span>
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span
                className="block text-outline"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              >
                Running.
              </motion.span>
            </span>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-base md:text-lg text-slate-400 leading-relaxed mt-8 max-w-2xl"
          >
            100+ projects delivered across 14 states for Government, PSUs, corporates and
            healthcare institutions. A representative selection below — the full project
            dossier is shared on request.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.65 }}
            className="flex flex-wrap gap-2 mt-12 mb-14"
            data-testid="project-filters"
          >
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`font-mono-accent text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 border transition-colors duration-300 ${
                  filter === f
                    ? "border-electric bg-electric/15 text-white"
                    : "border-white/10 text-slate-500 hover:text-slate-300 hover:border-white/25"
                }`}
                data-testid={`project-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {f}
              </button>
            ))}
          </motion.div>

          {projects === null ? (
            <div className="flex justify-center py-20" data-testid="projects-loading">
              <div className="w-6 h-6 border-2 border-electric border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <AnimatePresence mode="popLayout">
              {filtered.map((project, i) => (
                <motion.article
                  layout
                  key={project.title}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  className="group card-glow img-zoom border border-white/10 bg-navy-800/40"
                  data-testid={`project-card-${i}`}
                >
                  <Link to={`/projects/${project.slug}`} className="block" data-testid={`project-link-${i}`}>
                  <div className="relative h-52 lg:h-60 overflow-hidden">
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
                    <span className="absolute top-4 left-4 font-mono-accent text-[10px] tracking-[0.2em] uppercase text-neon bg-navy/70 px-3 py-1.5 border border-neon/20">
                      {project.division}
                    </span>
                    <span className="absolute top-4 right-4 font-mono-accent text-[10px] tracking-[0.2em] text-slate-300 bg-navy/70 px-3 py-1.5 border border-white/10">
                      {project.year}
                    </span>
                  </div>
                  <div className="p-6 lg:p-7">
                    <h3 className="font-display text-lg lg:text-xl font-bold tracking-tight">{project.title}</h3>
                    <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-electric mt-2 flex items-center gap-1.5">
                      <MapPin className="w-3 h-3" /> {project.location}
                    </p>
                    <p className="text-sm text-slate-400 leading-relaxed mt-4">{project.scope}</p>
                  </div>
                  </Link>
                </motion.article>
              ))}
            </AnimatePresence>
          </motion.div>
          )}

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-slate-600 mt-16 text-center"
            data-testid="projects-disclaimer"
          >
            Representative showcase — client names and detailed case studies shared on request
          </motion.p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
